import {
  CreditorBankAccountCreateRequestBody,
  CreditorDetailResource,
  CreditorType,
} from "@gocardless/api/dashboard/types";
import {
  Field,
  Input,
  Hint,
  FormFieldStatus,
  Label,
  Select,
  Option,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";

import { getBankAccountHolderDetails } from "src/components/routes/Setup/verification-status/helpers/getBankAccountHolderDetails";
import { FieldName } from "src/config";

interface ExperimentsEnabled {
  copEnabled: boolean;
  pennyTestImprovementsEnabled: boolean;
}

interface AccountHolderNameFieldProps {
  updateDetails: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  experimentsEnabled: ExperimentsEnabled;
  creditorType: CreditorType | undefined;
  creditorDetails: CreditorDetailResource | undefined;
  accountHolderName: string;
}
export const AccountHolderNameField = ({
  updateDetails,
  experimentsEnabled: { copEnabled, pennyTestImprovementsEnabled },
  creditorType,
  creditorDetails,
  accountHolderName,
}: AccountHolderNameFieldProps) => {
  const {
    register,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<CreditorBankAccountCreateRequestBody>();

  //Extract sole trader name and trading name from creditor details
  const individualInfo =
    creditorType &&
    creditorDetails?.detail &&
    getBankAccountHolderDetails(creditorType, creditorDetails.detail);

  //Create array from name values and filter out any undefined values
  const individualNameValues = [
    individualInfo?.personName,
    individualInfo?.tradingName,
  ].filter((value): value is string => !!value);

  //show select if included in Penny Test Improvements experiment && sole trader && has creditor name and trading name
  const showSelectField =
    pennyTestImprovementsEnabled &&
    creditorType === CreditorType.Individual &&
    individualNameValues.length > 1;

  //show no field if only 1 name present for sole trader or entity type is anything other than sole trader, or included in COP experiment
  const showNoAccountHolderNameField =
    (copEnabled && creditorType === CreditorType.Company) ||
    (pennyTestImprovementsEnabled &&
      (creditorType !== CreditorType.Individual ||
        individualNameValues.length === 1));

  useEffect(() => {
    //maintain current behaviour
    if (!pennyTestImprovementsEnabled && !copEnabled) {
      setValue(
        FieldName.accountName,
        (accountHolderName || creditorDetails?.detail?.name) ?? ""
      );
    }
    //sets the default value if the creditor type is company, charity, partnership, trust as name is stored
    // on creditorDetails?.detail?.name and included in one of our experiments
    if (
      (pennyTestImprovementsEnabled || copEnabled) &&
      creditorType !== CreditorType.Individual
    ) {
      setValue(FieldName.accountName, creditorDetails?.detail?.name ?? "");
    }
    //set the default value if individual without trading name and including penny test experiment
    //If has trading name, the default is set on the select component directly
    if (
      pennyTestImprovementsEnabled &&
      individualNameValues.length === 1 &&
      individualNameValues[0]
    ) {
      setValue(FieldName.accountName, individualNameValues[0]);
    }
  }, [
    accountHolderName,
    copEnabled,
    creditorDetails?.detail?.name,
    creditorType,
    individualNameValues,
    pennyTestImprovementsEnabled,
    setValue,
  ]);

  if (showNoAccountHolderNameField) {
    return null;
  }

  return (
    <Field>
      <Label htmlFor={FieldName.accountName}>
        <Trans id="setup.bank-details.add-bank-form.account-holder-name">
          Account holder name
        </Trans>
      </Label>
      {showSelectField ? (
        <Select
          id={FieldName.accountName}
          {...register(FieldName.accountName, {
            onChange: updateDetails,
            required: true,
          })}
          value={getValues(FieldName.accountName) || individualNameValues[0]}
          className="fs-exclude"
        >
          {individualNameValues.map((name) => (
            <Option key={name} value={name}>
              {name}
            </Option>
          ))}
        </Select>
      ) : (
        <>
          <Input
            id={FieldName.accountName}
            {...register(FieldName.accountName, {
              onChange: updateDetails,
              required: true,
              validate: (value: string) => value.trim() && value.length > 2,
            })}
            className="fs-exclude"
          />
          {errors[FieldName.accountName] && (
            <Hint status={FormFieldStatus.Danger}>
              <Trans id="setup.bank-details.add-bank-form.provide_account_name">
                Please provide a valid account name
              </Trans>
            </Hint>
          )}
        </>
      )}
    </Field>
  );
};
